import { useReducedMotion } from 'motion/react'
import type { CustomerAccount, CustomerProfile } from '../types'
import type { CustomerOrder } from '../customerOrdersApi'
import type { CustomerSupportRequest } from '../customerSupportApi'
import type { CustomerPrivacyDashboard } from '../customerPrivacyApi'
import { useShopI18n } from '../../i18n'
import { CustomerAreaLayoutShell } from './customer-area'
import {
  DashboardActivitySection,
  DashboardKpiSection,
  DashboardQuickActionsSection,
  DashboardRecentOrdersSection,
  DashboardTrackingSection,
  buildDashboardSupportPath,
} from './dashboard'
import { useCustomerDashboardController } from './dashboard/useCustomerDashboardController'

type CustomerDashboardPageProps = {
  account: CustomerAccount
  profile: CustomerProfile
  orders: CustomerOrder[]
  ordersLoading: boolean
  ordersError?: string
  supportRequests: CustomerSupportRequest[]
  supportLoading: boolean
  supportError?: string
  privacyDashboard: CustomerPrivacyDashboard | null
  onNavigate: (targetPath: string) => void
  onLogout: () => void
}

export const CustomerDashboardPage = ({
  account,
  profile,
  orders,
  ordersLoading,
  ordersError,
  supportRequests,
  supportLoading,
  supportError,
  privacyDashboard,
  onNavigate,
  onLogout,
}: CustomerDashboardPageProps) => {
  const { language, locale, t, toLocalizedPath } = useShopI18n()
  const reducedMotion = Boolean(useReducedMotion())

  const {
    isItalian,
    summary,
    navItems,
    localMeta,
    quickActionCards,
    kpiShortcuts,
    recentOrders,
    selectedOrderId,
    setSelectedOrderId,
    selectedOrder,
    selectedOrderProgress,
    selectedOrderNextStep,
    activityItems,
    activityDateFormatter,
    isDashboardBootstrapLoading,
  } = useCustomerDashboardController({
    account,
    profile,
    orders,
    ordersLoading,
    supportRequests,
    supportLoading,
    privacyDashboard,
    language,
    locale,
    t,
    toLocalizedPath,
    onNavigate,
  })

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={isItalian ? `Ciao, ${summary.displayName}` : `Hi, ${summary.displayName}`}
      subtitle={
        isItalian
          ? 'Gestisci ordini, indirizzi, pagamenti e impostazioni del tuo account.'
          : 'Manage orders, addresses, payments, and account settings from one place.'
      }
      meta={localMeta}
      navItems={navItems}
      activeNavId="overview"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'go-orders',
          label: isItalian ? 'Apri ordini' : 'Open orders',
          onClick: () => onNavigate(toLocalizedPath('/dashboard/orders')),
          variant: 'secondary',
        },
        {
          id: 'logout',
          label: t('common.logout'),
          onClick: onLogout,
          variant: 'danger',
        },
      ]}
    >
      <DashboardQuickActionsSection
        isBootstrapLoading={isDashboardBootstrapLoading}
        reducedMotion={reducedMotion}
        quickActionCards={quickActionCards}
        isItalian={isItalian}
      />

      <DashboardKpiSection
        isBootstrapLoading={isDashboardBootstrapLoading}
        reducedMotion={reducedMotion}
        kpiShortcuts={kpiShortcuts}
      />

      {(ordersError || supportError) && !ordersLoading ? (
        <div className="grid gap-2 lg:grid-cols-2">
          {ordersError ? (
            <div className="rounded-xl border border-(--color-status-error-border) bg-(--color-status-error-bg) px-3 py-2 text-sm text-(--color-status-error)">
              {ordersError}
            </div>
          ) : null}
          {supportError ? (
            <div className="rounded-xl border border-(--color-status-warning-border) bg-(--color-status-warning-bg) px-3 py-2 text-sm text-(--color-status-warning)">
              {supportError}
            </div>
          ) : null}
        </div>
      ) : null}

      <section className="grid gap-3 xl:grid-cols-[minmax(0,1.3fr)_minmax(360px,1fr)]">
        <DashboardRecentOrdersSection
          reducedMotion={reducedMotion}
          ordersLoading={ordersLoading}
          recentOrders={recentOrders}
          selectedOrderId={selectedOrderId}
          language={language}
          locale={locale}
          isItalian={isItalian}
          t={t}
          onOpenHistory={() => onNavigate(toLocalizedPath('/dashboard/orders'))}
          onOpenCatalog={() => onNavigate(toLocalizedPath('/'))}
          onSelectOrder={setSelectedOrderId}
          onOpenOrderDetail={(orderId) => onNavigate(toLocalizedPath(`/dashboard/orders/${encodeURIComponent(orderId)}`))}
          onRequestSupport={(order) => onNavigate(buildDashboardSupportPath(order, toLocalizedPath))}
        />

        <DashboardTrackingSection
          reducedMotion={reducedMotion}
          ordersLoading={ordersLoading}
          selectedOrder={selectedOrder}
          selectedOrderProgress={selectedOrderProgress}
          selectedOrderNextStep={selectedOrderNextStep}
          language={language}
          locale={locale}
          isItalian={isItalian}
          t={t}
          onOpenOrderDetail={(orderId) => onNavigate(toLocalizedPath(`/dashboard/orders/${encodeURIComponent(orderId)}`))}
          onOpenCatalog={() => onNavigate(toLocalizedPath('/'))}
          onRequestSupport={(order) => onNavigate(buildDashboardSupportPath(order, toLocalizedPath))}
        />
      </section>

      <DashboardActivitySection
        reducedMotion={reducedMotion}
        supportLoading={supportLoading}
        activityItems={activityItems}
        activityDateFormatter={activityDateFormatter}
        isItalian={isItalian}
        t={t}
      />
    </CustomerAreaLayoutShell>
  )
}
